import { Reference } from '@approvalmax/types';
import { Checkbox, DateTimeEditor, FocusImplementer, NumberEditor, TextAreaEditor } from '@approvalmax/ui';
import { intl, numberHelpers } from '@approvalmax/utils';
import { constants } from 'modules/common';
import { domain } from 'modules/data';
import { ReadOnlyCell } from 'pages/requestForm/components';
import { NetSuiteSalesTaxItem } from 'pages/requestForm/data/netsuite/NetSuiteContext';

import { ColumnDefinition } from '../../../../selectors/types/ColumnDefinition';
import { NetSuiteJournalEntryLineExpanded } from '../../data/types';
import {
    checkLineAmortizationDateIsInvalid,
    isFieldMandatory,
    isFieldReadOnly,
    isFieldVisible,
} from '../../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { NetSuiteLineItemAmortizationScheduleCell } from '../NetSuiteLineItemAmortizationScheduleCell/NetSuiteLineItemAmortizationScheduleCell';
import { NetSuiteLineItemDropdownCell } from '../NetSuiteLineItemDropdownCell/NetSuiteLineItemDropdownCell';
import { NetSuiteLineItemTaxAccountCell } from '../NetSuiteLineItemTaxAccountCell/NetSuiteLineItemTaxAccountCell';
import { NetSuiteLineItemTaxCodeCell } from '../NetSuiteLineItemTaxCodeCell/NetSuiteLineItemTaxCodeCell';
import { messages } from './NetSuiteJournalEntryLines.messages';
import { DropdownEditorContainer } from './NetSuiteJournalEntryLines.styles';
import { ExtendedColumnDefinition, JournalEntryLinesColumnOptions } from './NetSuiteJournalEntryLines.types';

const { netSuiteConstants } = constants;

export const getColumnDefinitions = ({
    showErrors,
    showErrorsOnEmptyLine,
    fieldSettings,
    accounts,
    classes,
    departments,
    locations,
    isApproverMode,
    currencyPrecision,
    customFieldsColumnDefinition,
    onFieldUpdate,
    onMultiLineFieldUpdate,
}: JournalEntryLinesColumnOptions): ColumnDefinition<NetSuiteJournalEntryLineExpanded>[] => {
    const columnDef: ExtendedColumnDefinition[] = [
        ...(isApproverMode
            ? []
            : [
                  {
                      id: 'checkbox',
                      name: '',
                      fixedWidth: 40,
                      headerCell: (lineItems) => {
                          const checked = lineItems.every((line) => line.checked);
                          const triState = lineItems.some((line) => line.checked);

                          const handleClick = () => {
                              const nextState = !checked;

                              onMultiLineFieldUpdate('checked', nextState);
                          };

                          return (
                              <div onClick={handleClick}>
                                  <Checkbox size='size-18' triState={triState} checked={checked} />
                              </div>
                          );
                      },
                      cell: (lineItem) => {
                          return (
                              <div
                                  onClick={() => {
                                      onFieldUpdate(lineItem.id, 'checked', !lineItem.checked);
                                  }}
                              >
                                  <Checkbox checked={lineItem.checked} size='size-18' />
                              </div>
                          );
                      },
                  } as ColumnDefinition<NetSuiteJournalEntryLineExpanded>,
              ]),

        {
            id: 'account',
            name: messages.accountColumnName,
            fixedWidth: 218,
            invalid: (li) => (showErrors && !li.isEmpty && !li.account) || (showErrors && showErrorsOnEmptyLine),
            cell: (li, { invalid }) => {
                return (
                    <DropdownEditorContainer>
                        <NetSuiteLineItemDropdownCell
                            items={accounts}
                            invalid={invalid}
                            value={li.account || null}
                            singleLinePerItem={false}
                            onChange={(value: Reference | null) => {
                                onFieldUpdate(li.id, 'account', value);
                            }}
                            placeholder={messages.accountFieldPlaceholder}
                        />
                    </DropdownEditorContainer>
                );
            },
        },

        {
            id: 'debitAmount',
            name: messages.debitAmountColumnName,
            alignRight: true,
            fixedWidth: 274,
            invalid: (li) => {
                if (!showErrors) {
                    return false;
                }

                if (!li.isEmpty && !li.debitAmount && !li.creditAmount) {
                    return true;
                }

                return showErrorsOnEmptyLine;
            },
            cell: (li, { invalid }) => (
                <FocusImplementer>
                    {({ registerFocusable }) => (
                        <NumberEditor
                            ref={registerFocusable}
                            allowFloat
                            invalid={invalid}
                            value={li.debitAmount}
                            onChange={(newValue: number | null) => {
                                onFieldUpdate(li.id, 'debitAmount', newValue);
                            }}
                            placeholder={messages.defaultInputPlaceholder}
                            theme='transparent'
                            align='right'
                            displayFormat='currency-auto'
                            max={netSuiteConstants.maxTotalAmount}
                            min={-netSuiteConstants.maxTotalAmount}
                            precision={currencyPrecision}
                            allowCalculation
                        />
                    )}
                </FocusImplementer>
            ),
        },

        {
            id: 'creditAmount',
            name: messages.creditAmountColumnName,
            alignRight: true,
            fixedWidth: 274,
            invalid: (li) => {
                if (!showErrors) {
                    return false;
                }

                if (!li.isEmpty && !li.debitAmount && !li.creditAmount) {
                    return true;
                }

                return showErrorsOnEmptyLine;
            },
            cell: (li, { invalid }) => (
                <FocusImplementer>
                    {({ registerFocusable }) => (
                        <NumberEditor
                            ref={registerFocusable}
                            allowFloat
                            invalid={invalid}
                            value={li.creditAmount}
                            onChange={(newValue: number | null) => {
                                onFieldUpdate(li.id, 'creditAmount', newValue);
                            }}
                            placeholder={messages.defaultInputPlaceholder}
                            theme='transparent'
                            align='right'
                            displayFormat='currency-auto'
                            max={netSuiteConstants.maxTotalAmount}
                            min={-netSuiteConstants.maxTotalAmount}
                            precision={currencyPrecision}
                            allowCalculation
                        />
                    )}
                </FocusImplementer>
            ),
        },

        {
            id: 'taxCode',
            name: messages.taxCodeColumnName,
            fixedWidth: 160,
            isHidden: !isFieldVisible(fieldSettings.itemsTaxCode),
            readonly: () => isFieldReadOnly(fieldSettings.itemsTaxCode),
            invalid: (li) =>
                showErrors &&
                ((isFieldMandatory(fieldSettings.itemsTaxCode) && !li.isEmpty && !li.taxCode) || showErrorsOnEmptyLine),
            cell: (li, { invalid, readonly }) =>
                readonly ? (
                    <ReadOnlyCell text={li.taxCode?.text ?? ''} />
                ) : (
                    <DropdownEditorContainer>
                        <NetSuiteLineItemTaxCodeCell
                            invalid={invalid}
                            value={li.taxCode}
                            singleLinePerItem={false}
                            onChange={(newValue: NetSuiteSalesTaxItem | null) => {
                                onFieldUpdate(li.id, 'taxCode', newValue);
                            }}
                            placeholder={messages.taxCodeFieldPlaceholder}
                        />
                    </DropdownEditorContainer>
                ),
        },
        {
            id: 'taxRate',
            name: messages.taxRateColumnName,
            alignRight: true,
            fixedWidth: 160,
            isHidden: !isFieldVisible(fieldSettings.itemsTaxRate),
            readonly: () => true,
            invalid: () => showErrors && showErrorsOnEmptyLine,
            cell: (li) => <ReadOnlyCell text={numberHelpers.isNumber(li.taxRate) ? `${li.taxRate}%` : ''} />,
        },
        {
            id: 'taxAmount',
            name: messages.taxAmountColumnName,
            alignRight: true,
            fixedWidth: 160,
            isHidden: !isFieldVisible(fieldSettings.itemsTaxAmount),
            invalid: (li) => {
                if (!numberHelpers.isNumber(li.taxAmount)) {
                    return showErrors && showErrorsOnEmptyLine;
                }

                let isInvalidTaxAmount = false;

                if (li.taxAmount > netSuiteConstants.taxAmountMaxValue) {
                    isInvalidTaxAmount = true;
                } else {
                    const taxAmountDigitsCount = li.taxAmount.toString().replace(/[^\d]/g, '').length;

                    if (taxAmountDigitsCount > netSuiteConstants.taxAmountMaxDigitsCount) {
                        isInvalidTaxAmount = true;
                    }
                }

                return showErrors && isInvalidTaxAmount;
            },
            readonly: () => true,
            cell: (li) => (
                <ReadOnlyCell text={li.taxAmount !== null ? intl.formatNumber(li.taxAmount, currencyPrecision) : ''} />
            ),
        },
        {
            id: 'grossAmount',
            name: messages.grossAmountColumnName,
            alignRight: true,
            fixedWidth: 160,
            invalid: (li) => showErrors && (!numberHelpers.isNumber(li.grossAmount) || showErrorsOnEmptyLine),
            readonly: () => isFieldReadOnly(fieldSettings.itemsGrossAmount),
            isHidden: !isFieldVisible(fieldSettings.itemsGrossAmount),
            cell: (li, { readonly, invalid }) =>
                readonly ? (
                    <ReadOnlyCell
                        text={li.grossAmount !== null ? intl.formatNumber(li.grossAmount, currencyPrecision) : ''}
                    />
                ) : (
                    <FocusImplementer>
                        {({ registerFocusable }) => (
                            <NumberEditor
                                ref={registerFocusable}
                                allowFloat
                                invalid={invalid}
                                value={li.grossAmount}
                                onChange={(newValue: number | null) => {
                                    onFieldUpdate(li.id, 'grossAmount', newValue);
                                }}
                                placeholder={messages.defaultInputPlaceholder}
                                theme='transparent'
                                align='right'
                                displayFormat='currency-auto'
                                max={netSuiteConstants.maxTotalAmount}
                                min={-netSuiteConstants.maxTotalAmount}
                                precision={currencyPrecision}
                                allowCalculation
                            />
                        )}
                    </FocusImplementer>
                ),
        },
        {
            id: 'taxAccount',
            name: messages.taxAccountColumnName,
            fixedWidth: 160,
            isHidden: !isFieldVisible(fieldSettings.itemsTaxAccount),
            readonly: () => isFieldReadOnly(fieldSettings.itemsTaxAccount),
            invalid: (li) =>
                showErrors &&
                ((isFieldMandatory(fieldSettings.itemsTaxAccount) && !li.isEmpty && !li.taxAccount) ||
                    showErrorsOnEmptyLine),
            cell: (li, { invalid, readonly }) =>
                readonly ? (
                    <ReadOnlyCell text={li.taxAccount?.text || ''} />
                ) : (
                    <DropdownEditorContainer>
                        <NetSuiteLineItemTaxAccountCell
                            invalid={invalid}
                            value={li.taxAccount}
                            singleLinePerItem={false}
                            onChange={(value: Reference | null) => {
                                onFieldUpdate(li.id, 'taxAccount', value);
                            }}
                            placeholder={messages.taxAccountFieldPlaceholder}
                            integrationCode={domain.IntegrationCode.NetSuiteJournalEntry}
                        />
                    </DropdownEditorContainer>
                ),
        },
        {
            id: 'memo',
            name: messages.memoColumnName,
            fixedWidth: 274,
            isHidden: !isFieldVisible(fieldSettings.memo),
            readonly: () => isFieldReadOnly(fieldSettings.memo),
            invalid: (li) => {
                if (!showErrors) {
                    return false;
                }

                if (isFieldMandatory(fieldSettings.itemsMemo) && !li.memo && !li.isEmpty) {
                    return true;
                }

                return showErrorsOnEmptyLine;
            },
            cell: (li, { invalid, readonly }) =>
                readonly ? (
                    <ReadOnlyCell text={li.memo || ''} multiline />
                ) : (
                    <FocusImplementer>
                        {({ registerFocusable }) => (
                            <TextAreaEditor
                                ref={registerFocusable}
                                invalid={invalid}
                                value={li.memo || ''}
                                onChange={(value) => {
                                    onFieldUpdate(li.id, 'memo', value);
                                }}
                                placeholder={messages.memoFieldPlaceholder}
                                theme='transparent'
                                maxLength={netSuiteConstants.memoMaxLength}
                                changeOnBlur
                            />
                        )}
                    </FocusImplementer>
                ),
        },
        {
            id: 'class',
            name: messages.classColumnName,
            fixedWidth: 180,
            isHidden: !isFieldVisible(fieldSettings.itemsClass),
            readonly: () => isFieldReadOnly(fieldSettings.itemsClass),
            invalid: (li) =>
                showErrors &&
                ((isFieldMandatory(fieldSettings.itemsClass) && !li.class && !li.isEmpty) || showErrorsOnEmptyLine),
            cell: (li, { invalid, readonly }) =>
                readonly ? (
                    <ReadOnlyCell text={li.class?.text || ''} />
                ) : (
                    <DropdownEditorContainer>
                        <NetSuiteLineItemDropdownCell
                            items={classes}
                            invalid={invalid}
                            value={li.class}
                            singleLinePerItem={false}
                            onChange={(value: domain.NetSuiteItemLine['class']) => {
                                onFieldUpdate(li.id, 'class', value);
                            }}
                            placeholder={messages.classFieldPlaceholder}
                        />
                    </DropdownEditorContainer>
                ),
        },
        {
            id: 'department',
            name: messages.departmentColumnName,
            fixedWidth: 274,
            isHidden: !isFieldVisible(fieldSettings.itemsDepartment),
            readonly: () => isFieldReadOnly(fieldSettings.itemsDepartment),
            invalid: (li) =>
                showErrors &&
                ((isFieldMandatory(fieldSettings.itemsDepartment) && !li.department && !li.isEmpty) ||
                    showErrorsOnEmptyLine),
            cell: (li, { invalid, readonly }) =>
                readonly ? (
                    <ReadOnlyCell text={li.department?.text || ''} />
                ) : (
                    <DropdownEditorContainer>
                        <NetSuiteLineItemDropdownCell
                            items={departments}
                            invalid={invalid}
                            value={li.department}
                            singleLinePerItem={false}
                            onChange={(value: domain.NetSuiteItemLine['department']) => {
                                onFieldUpdate(li.id, 'department', value);
                            }}
                            placeholder={messages.departmentFieldPlaceholder}
                        />
                    </DropdownEditorContainer>
                ),
        },
        {
            id: 'location',
            name: messages.locationColumnName,
            fixedWidth: 180,
            isHidden: !isFieldVisible(fieldSettings.itemsLocation),
            readonly: () => isFieldReadOnly(fieldSettings.itemsLocation),
            invalid: (li) =>
                showErrors &&
                ((isFieldMandatory(fieldSettings.itemsLocation) && !li.location && !li.isEmpty) ||
                    showErrorsOnEmptyLine),
            cell: (li, { invalid, readonly }) =>
                readonly ? (
                    <ReadOnlyCell text={li.location?.text || ''} />
                ) : (
                    <DropdownEditorContainer>
                        <NetSuiteLineItemDropdownCell
                            items={locations}
                            invalid={invalid}
                            value={li.location}
                            singleLinePerItem={false}
                            onChange={(value: domain.NetSuiteItemLine['location']) => {
                                onFieldUpdate(li.id, 'location', value);
                            }}
                            placeholder={messages.locationFieldPlaceholder}
                        />
                    </DropdownEditorContainer>
                ),
        },
        {
            id: 'amortizationSchedule',
            name: messages.amortizationSchedule,
            fixedWidth: 218,
            isHidden: !isFieldVisible(fieldSettings.itemsAmortizationSchedule),
            readonly: () => isFieldReadOnly(fieldSettings.itemsAmortizationSchedule),

            invalid: (li) => {
                if (!showErrors) {
                    return false;
                }

                if (
                    isFieldMandatory(fieldSettings.itemsAmortizationSchedule) &&
                    !li.amortizationSchedule &&
                    !li.isEmpty
                ) {
                    return true;
                }

                return showErrorsOnEmptyLine;
            },
            cell: (li, { invalid, readonly }) =>
                readonly ? (
                    <ReadOnlyCell text={li.amortizationSchedule?.text ?? ''} />
                ) : (
                    <DropdownEditorContainer>
                        <NetSuiteLineItemAmortizationScheduleCell
                            invalid={invalid}
                            value={li.amortizationSchedule || null}
                            singleLinePerItem={false}
                            apiVersion='v2'
                            onChange={(value) => {
                                onFieldUpdate(li.id, 'amortizationSchedule', value);
                            }}
                            placeholder={messages.customerFieldPlaceholder}
                            integrationCode={domain.IntegrationCode.NetSuiteJournalEntry}
                        />
                    </DropdownEditorContainer>
                ),
        },
        {
            id: 'amortizationStartDate',
            name: messages.amortizationStartDate,
            fixedWidth: 218,
            isHidden: !isFieldVisible(fieldSettings.itemsAmortizationStartDate),
            readonly: () => isFieldReadOnly(fieldSettings.itemsAmortizationStartDate),
            invalid: (li) => {
                if (!showErrors) {
                    return false;
                }

                if (checkLineAmortizationDateIsInvalid(li)) {
                    return true;
                }

                if (
                    isFieldMandatory(fieldSettings.itemsAmortizationStartDate) &&
                    !li.amortizationStartDate &&
                    !li.isEmpty
                ) {
                    return true;
                }

                return showErrorsOnEmptyLine;
            },
            cell: (li, { invalid, readonly }) =>
                readonly ? (
                    <ReadOnlyCell text={li.amortizationStartDate ?? ''} />
                ) : (
                    <FocusImplementer>
                        {({ registerFocusable }) => (
                            <DateTimeEditor
                                ref={registerFocusable}
                                value={li.amortizationStartDate || null}
                                placeholder={messages.datePlaceholder}
                                onChange={(value) => onFieldUpdate(li.id, 'amortizationStartDate', value)}
                                theme='transparent'
                                invalid={invalid}
                            />
                        )}
                    </FocusImplementer>
                ),
        },
        {
            id: 'amortizationEndDate',
            name: messages.amortizationEndDate,
            fixedWidth: 218,
            isHidden: !isFieldVisible(fieldSettings.itemsAmortizationEndDate),
            readonly: () => isFieldReadOnly(fieldSettings.itemsAmortizationEndDate),
            invalid: (li) => {
                if (!showErrors) {
                    return false;
                }

                if (checkLineAmortizationDateIsInvalid(li)) {
                    return true;
                }

                if (
                    isFieldMandatory(fieldSettings.itemsAmortizationEndDate) &&
                    !li.amortizationEndDate &&
                    !li.isEmpty
                ) {
                    return true;
                }

                return showErrorsOnEmptyLine;
            },
            cell: (li, { invalid, readonly }) =>
                readonly ? (
                    <ReadOnlyCell text={li.amortizationStartDate ?? ''} />
                ) : (
                    <FocusImplementer>
                        {({ registerFocusable }) => (
                            <DateTimeEditor
                                ref={registerFocusable}
                                value={li.amortizationEndDate || null}
                                placeholder={messages.datePlaceholder}
                                onChange={(value) => onFieldUpdate(li.id, 'amortizationEndDate', value)}
                                theme='transparent'
                                invalid={invalid}
                            />
                        )}
                    </FocusImplementer>
                ),
        },
        {
            id: 'amortizationResidual',
            name: messages.amortizationResidual,
            alignRight: true,
            fixedWidth: 160,
            isHidden: !isFieldVisible(fieldSettings.itemsAmortizationResidual),
            readonly: () => isFieldReadOnly(fieldSettings.itemsAmortizationResidual),
            invalid: (li) => {
                if (!showErrors) {
                    return false;
                }

                if (
                    isFieldMandatory(fieldSettings.itemsAmortizationResidual) &&
                    typeof li.amortizationResidual !== 'number' &&
                    !li.isEmpty
                ) {
                    return true;
                }

                return showErrorsOnEmptyLine;
            },
            cell: (li, { invalid, readonly }) => {
                return readonly ? (
                    <ReadOnlyCell
                        text={
                            typeof li.amortizationResidual === 'number'
                                ? intl.formatNumber(li.amortizationResidual, currencyPrecision)
                                : ''
                        }
                    />
                ) : (
                    <FocusImplementer>
                        {({ registerFocusable }) => (
                            <NumberEditor
                                ref={registerFocusable}
                                allowFloat
                                invalid={invalid}
                                value={typeof li.amortizationResidual === 'number' ? li.amortizationResidual : null}
                                onChange={(newValue: number | null) => {
                                    onFieldUpdate(li.id, 'amortizationResidual', newValue);
                                }}
                                placeholder={messages.defaultInputPlaceholder}
                                theme='transparent'
                                align='right'
                                max={netSuiteConstants.maxTotalAmount}
                                min={-netSuiteConstants.maxTotalAmount}
                                precision={currencyPrecision}
                                displayFormat='currency'
                                changeOnBlur
                            />
                        )}
                    </FocusImplementer>
                );
            },
        },
        ...customFieldsColumnDefinition,
    ];

    return columnDef.filter((col) => !col.isHidden);
};
